import {
  BadgeIndianRupee,
  ChevronDown,
  LogOut,
  Menu,
  MessageSquare,
  Settings,
  X,
} from "lucide-react";
import {
  Crm,
  Staff,
  Bank,
  Affiliate,
  Report,
  Dashboard,
  Logout as LogoutIcon,
  Finance,
} from "../../assets/Icons";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSidebar } from "../../context/SidebarContext";
import { useAuth } from "../../context/AuthContext";
import { toast } from "react-toastify";
import axios from "axios";
import NotificationPage from "../../components/Notification";
import MessageNotification from "../../components/MessageNotification";
import { notificationAPI } from "../../services/notificationAPI";
import { getUnreadMessageNotificationCount } from "../../services/messageNotificationAPI";
import { socketService } from "../../services/communicationAPI";

// NavItem component for mobile menu
const NavItem = ({
  to,
  icon,
  label,
  active,
  handleNavigation,
  logout = false,
}) => {
  return (
    <li>
      <button
        onClick={() => handleNavigation(to)}
        className={`flex items-center justify-start
         w-full px-3 py-2 cursor-pointer ${
           active ? "text-black bg-[#FFD700]" : "text-[#FFD700] "
         }
        transition-colors relative`}
      >
        <span className={`mr-3 ${logout ? "text-red-500" : "text-[#FFCA20]"}`}>
          {icon}
        </span>
        <span>{label}</span>
      </button>
    </li>
  );
};

const TopNavbar = () => {
  const {
    sidebarExpanded,
    toggleSidebar,
    isMobile,
    mobileMenuOpen,
    setMobileMenuOpen,
  } = useSidebar();
  const { user, logout } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [messageNotificationOpen, setMessageNotificationOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [messageNotificationCount, setMessageNotificationCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  const role = user?.role || "customer";

  //dropdown handling
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);
  const notificationRef = useRef(null);
  const messageNotificationRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setNotificationOpen(false);
      }
      if (
        messageNotificationRef.current &&
        !messageNotificationRef.current.contains(event.target)
      ) {
        setMessageNotificationOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch notification counts on initial load
  useEffect(() => {
    if (user?.id) {
      fetchNotificationCount();
      fetchMessageNotificationCount();
    }
  }, [user?.id]);

  // Socket event handling for message notifications
  useEffect(() => {
    const handleMessageNotificationReceived = (data) => {
      // Update the total message notification count when a new message notification is received
      setMessageNotificationCount((prev) => prev + 1);
    };

    const handleMessageNotificationRead = () => {
      // Decrease count when a message notification is marked as read
      setMessageNotificationCount((prev) => Math.max(0, prev - 1));
    };

    // Add socket event listeners
    socketService.on(
      "messageNotificationReceived",
      handleMessageNotificationReceived
    );
    socketService.on("messageNotificationRead", handleMessageNotificationRead);

    // Cleanup
    return () => {
      socketService.off(
        "messageNotificationReceived",
        handleMessageNotificationReceived
      );
      socketService.off(
        "messageNotificationRead",
        handleMessageNotificationRead
      );
    };
  }, []);

  const fetchNotificationCount = async () => {
    try {
      const res = await notificationAPI.getUnreadCount(user.id);
      if (res.data && res.data.unreadCount !== undefined) {
        setNotificationCount(res.data.unreadCount);
      }
    } catch (error) {
      console.error("Error fetching notification count:", error);
    }
  };

  const fetchMessageNotificationCount = async () => {
    try {
      const res = await getUnreadMessageNotificationCount();
      if (res.success && res.unreadCount !== undefined) {
        setMessageNotificationCount(res.unreadCount);
      }
    } catch (error) {
      console.error("Error fetching message notification count:", error);
    }
  };

  // Handle notification count change from Notification component
  const handleNotificationCountChange = useCallback((count) => {
    setNotificationCount(count);
  }, []);

  // Handle message notification count change from MessageNotification component
  const handleMessageNotificationCountChange = useCallback((count) => {
    setMessageNotificationCount(count);
  }, []);

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
  };

  const toggleNotification = () => {
    setNotificationOpen(!notificationOpen);
  };

  const toggleMessageNotification = () => {
    setMessageNotificationOpen(!messageNotificationOpen);
  };

  // Close mobile menu when switching tabs/windows or route changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    const handleBeforeUnload = () => {
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    // Close other dropdowns when mobile menu opens
    if (mobileMenuOpen && notificationOpen) {
      setNotificationOpen(false);
    }
    if (mobileMenuOpen && messageNotificationOpen) {
      setMessageNotificationOpen(false);
    }

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Cleanup event listeners
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [
    mobileMenuOpen,
    setMobileMenuOpen,
    notificationOpen,
    messageNotificationOpen,
  ]);

  // Close mobile menu when location changes
  useEffect(() => {
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  }, [location.pathname, setMobileMenuOpen]);

  const handleNavigation = (path) => {
    if (path === "logout") {
      handleLogout();
      return;
    }

    const basePath = `/${role}`;
    const routeMap = {
      dashboard: `${basePath}/dashboard`,
      "loan-applications": `${basePath}/customers`,
      "banking-partner": `${basePath}/banking-partner`,
      "affiliate-partners": `${basePath}/affiliates`,
      leads: `${basePath}/leads`,
      communication: `${basePath}/communication`,
      settings:
        role === "affiliate-partner"
          ? `${basePath}/profile`
          : `${basePath}/settings`,
      staff: `${basePath}/staff`,
      reports: `${basePath}/reports`,
      financials: `${basePath}/financials`,
      attendance: `${basePath}/attendance`,
      messages: `${basePath}/messages`,
    };

    const targetPath = routeMap[path] || path;
    navigate(targetPath);
    if (isMobile) setMobileMenuOpen(false);
  };

  // Handle logout functionality
  const handleLogout = async () => {
    try {
      await logout();
      localStorage.clear();
      sessionStorage.clear();
      delete axios.defaults.headers.common["Authorization"];
      toast.success("Logged out successfully.");
       navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
      toast.error("Logout failed. Please try again.");
    }
  };

  const navItems = {
    admin: [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      { to: "staff", label: "Staff Management", icon: <Staff /> },
      { to: "banking-partner", label: "Banking Partners", icon: <Bank /> },
      {
        to: "affiliate-partners",
        label: "Affiliate Partners",
        icon: <Affiliate />,
      },
      { to: "financials", label: "Finance Dashboard", icon: <Finance /> },
      { to: "reports", label: "Sales Dashboard", icon: <Report /> },
      {
        to: "communication",
        label: "Communication Center",
        icon: <MessageSquare />,
      },
      { to: "messages", label: "Bulking Messages", icon: <MessageSquare /> },
    ],
    subAdmin: [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      { to: "staff", label: "Staff Management", icon: <Staff /> },
      { to: "banking-partner", label: "Banking Partners", icon: <Bank /> },
      {
        to: "affiliate-partners",
        label: "Affiliate Partners",
        icon: <Affiliate />,
      },
      { to: "financials", label: "Finance Dashboard", icon: <Finance /> },
      { to: "reports", label: "Sales Dashboard", icon: <Report /> },
      {
        to: "communication",
        label: "Communication Center",
        icon: <MessageSquare />,
      },
      { to: "messages", label: "Bulking Messages", icon: <MessageSquare /> },
    ],
    staff: [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      { to: "attendance", label: "Attendance", icon: <Affiliate /> },
    ],
    "banking-partner": [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      {
        to: "communication",
        label: "Communication Center",
        icon: <MessageSquare />,
      },
      { to: "settings", label: "Settings", icon: <Settings /> },
    ],
    "affiliate-partner": [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "leads", label: "Leads", icon: <BadgeIndianRupee /> },
      { to: "settings", label: "Settings", icon: <Settings /> },
    ],
    "credit-manager": [
      { to: "dashboard", label: "Dashboard", icon: <Dashboard /> },
      { to: "loan-applications", label: "CRM", icon: <Crm /> },
      { to: "banking-partner", label: "Banking Partners", icon: <Bank /> },
      {
        to: "communication",
        label: "Communication Center",
        icon: <MessageSquare />,
      },
    ],
  };

  const roleNavItems = navItems[role] || [];

  // Update navbar styling to take full width on mobile
  const navbarStyle = isMobile
    ? { width: "100%", marginLeft: 0 }
    : {
        marginLeft: sidebarExpanded ? "200px" : "60px",
        width: `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
      };

  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 z-20 bg-[#131313] border-b border-gray-800"
        style={navbarStyle}
      >
        <div className="px-4 py-3 flex justify-between items-center">
          {/* Left side - Hamburger menu for mobile */}
          <div className="flex items-center">
            {isMobile && (
              <button
                className="mr-4 text-gray-400 hover:text-white focus:outline-none"
                onClick={toggleSidebar}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            )}
          </div>

          {/* Right side - User and notifications */}
          <div className="flex items-center">
            {/* Message Notifications */}
            <button
              ref={messageNotificationRef}
              className="mr-3 relative text-gray-400 hover:text-white focus:outline-none"
              onClick={toggleMessageNotification}
            >
              <svg
                width="30"
                height="28"
                viewBox="0 0 46 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="0.5"
                  y="0.5"
                  width="45"
                  height="47"
                  rx="3.5"
                  fill={
                    messageNotificationOpen
                      ? "#ffca20"
                      : "rgba(255, 202, 32, 0.3)"
                  }
                  stroke="#FFCA20"
                />
                <path
                  d="M12 16C12 12.6863 14.6863 10 18 10H28C31.3137 10 34 12.6863 34 16V28C34 31.3137 31.3137 34 28 34H24L20 38L20 34H18C14.6863 34 12 31.3137 12 28V16Z"
                  fill={messageNotificationOpen ? "#423715" : "#ffca20"}
                />
                <circle
                  cx="20"
                  cy="22"
                  r="2"
                  fill={messageNotificationOpen ? "#ffca20" : "#423715"}
                />
                <circle
                  cx="26"
                  cy="22"
                  r="2"
                  fill={messageNotificationOpen ? "#ffca20" : "#423715"}
                />
                <circle
                  cx="32"
                  cy="22"
                  r="2"
                  fill={messageNotificationOpen ? "#ffca20" : "#423715"}
                />
              </svg>

              {/* Message notification count badge */}
              {messageNotificationCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-h-[20px] min-w-[20px] px-1 flex items-center justify-center border border-black shadow-md">
                  {messageNotificationCount > 9
                    ? "9+"
                    : messageNotificationCount}
                </span>
              )}
            </button>

            {/* Notifications */}
            {!["admin", "subAdmin"].includes(role) && (
              <button
                ref={notificationRef}
                className="mr-5 relative text-gray-400 hover:text-white focus:outline-none"
                onClick={toggleNotification}
              >
                <svg
                  width="30"
                  height="28"
                  viewBox="0 0 46 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="0.5"
                    y="0.5"
                    width="45"
                    height="47"
                    rx="3.5"
                    fill={
                      notificationOpen ? "#ffca20" : "rgba(255, 202, 32, 0.3)"
                    }
                    stroke="#FFCA20"
                  />
                  <path
                    d="M23 34.5C21.5938 34.5 20.5 33.4062 20.5 32H25.5C25.5 33.4062 24.3672 34.5 23 34.5ZM31.3984 28.6797C31.6328 28.9141 31.75 29.2266 31.75 29.5C31.7109 30.1641 31.2422 30.75 30.4609 30.75H15.5C14.7578 30.75 14.25 30.1641 14.25 29.5C14.25 29.2266 14.3281 28.9141 14.5625 28.6797C15.3047 27.8594 16.75 26.6484 16.75 22.625C16.75 19.6172 18.8594 17.1953 21.75 16.5703V15.75C21.75 15.0859 22.2969 14.5 23 14.5C23.6641 14.5 24.25 15.0859 24.25 15.75V16.5703C27.1016 17.1953 29.2109 19.6172 29.2109 22.625C29.2109 26.6484 30.6562 27.8594 31.3984 28.6797Z"
                    fill={notificationOpen ? "#423715" : "#ffca20"}
                  />
                </svg>

                {/* Notification count badge */}
                {notificationCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full min-h-[20px] min-w-[20px] px-1 flex items-center justify-center border border-black shadow-md">
                    {notificationCount > 9 ? "9+" : notificationCount}
                  </span>
                )}
              </button>
            )}

            {/* User profile */}
            {/* User profile */}
            <div
              className="flex items-center cursor-pointer relative" // added cursor-pointer + relative
              ref={dropdownRef}
              onClick={() => setIsOpen((prev) => !prev)} // moved toggle here
            >
              <div className="w-8 h-8 rounded-full bg-[#FFCA20] flex items-center justify-center text-black font-semibold overflow-hidden mr-2">
                <img
                  src="https://www.w3schools.com/howto/img_avatar.png"
                  alt="User"
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="hidden md:block">
                <div className="text-sm font-medium">
                  {user?.name || "User Name"}
                </div>
                <div className="text-xs text-gray-400 capitalize">{role}</div>
              </div>

              <ChevronDown className="ml-2 text-gray-400 h-4 w-4" />

              {/* user dropdown */}
              {isOpen && (
                <div className="absolute right-0 top-12 w-72 bg-[#131313] text-black rounded-xl shadow-xl z-50 overflow-hidden border border-gray-800">
                  {/* Profile Header */}
                  <div className="flex items-center gap-4 px-5 py-4 border-b border-gray-200 bg-[#131313]">
                    <img
                      src={
                        user.avatar ||
                        "https://www.w3schools.com/howto/img_avatar.png"
                      }
                      alt="Profile"
                      className="w-12 h-12 rounded-full object-cover border border-gray-300"
                    />
                    <div>
                      <p className="text-base font-semibold text-[#ffca20]">
                        {user.name}
                      </p>
                      <p className="text-sm text-gray-500 capitalize">
                        {user.role}
                      </p>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="px-5 py-4 space-y-3">
                    <div>
                      <p className="text-xs text-[#ffca20] uppercase">Email</p>
                      <p className="text-sm text-white">{user.email}</p>
                    </div>
                  </div>

                  {/* Logout */}
                  <div className="border-t border-gray-700">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-5 py-4 text-red-500 hover:bg-red-500 hover:text-white transition-colors"
                    >
                      <LogoutIcon size={18} />
                      <span>Log Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Notification component */}
      <NotificationPage
        isOpen={notificationOpen}
        onClose={() => setNotificationOpen(false)}
        onNotificationCountChange={handleNotificationCountChange}
      />

      {/* Message Notification component */}
      <MessageNotification
        isOpen={messageNotificationOpen}
        onClose={() => setMessageNotificationOpen(false)}
        onNotificationCountChange={handleMessageNotificationCountChange}
      />

      {/* Mobile Menu Drawer */}
      {mobileMenuOpen && isMobile && (
        <div className="fixed top-14 left-0 right-0 bottom-0 bg-black bg-opacity-90 z-40 overflow-y-auto">
          <div className="bg-[#131313] h-full p-4 space-y-2">
            {roleNavItems.map(({ to, label, icon }) => (
              <button
                key={to}
                onClick={() => handleNavigation(to)}
                className={`flex items-center w-full px-3 py-2 rounded text-left ${
                  location.pathname.includes(`/${to}`)
                    ? "bg-[#FFD700] text-black"
                    : "text-[#FFD700] hover:bg-[#FFD700] hover:text-black"
                }`}
              >
                <span className="mr-3">{icon}</span>
                {label}
              </button>
            ))}

            <button
              onClick={handleLogout}
              className="flex items-center w-full px-3 py-2 mt-4 text-red-500 hover:bg-red-500 hover:text-black rounded"
            >
              <LogoutIcon className="mr-3" size={20} />
              Log Out
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TopNavbar;
